import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import createSelector from 're-reselect';
import * as React from 'react';

import { BetaMark } from '../../components/BetaMark/BetaMark';
import { GridActionsMenuItem, GridActionsMenuItems } from '../../components/GridActionsMenu/GridActionsMenu';
import { DealActionsMenuContainerProps } from '../../containers/DealActionsMenuContainer/DealActionsMenuContainer';
import {
    GridActionsMenuContainerActions,
    GridActionsMenuContainerProps
} from '../../containers/GridActionsMenuContainer/GridActionsMenuContainer';
import { isBannerEditable } from '../../mappers/banner/banner';
import { isCampaignEditable } from '../../mappers/campaign';
import { isGroupEditable } from '../../mappers/group/group';
import { getInternalType } from '../../service/CampaignHelpers/CampaignHelpers';
import { checkIsCampaignUpdateAllowedInDna } from '../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { getTextForItemsCampaignsActionTooltipText } from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { AccessRights, banners, campaigns, feedFilters, groups, retargetings } from '../../typings/common';
import {
    AdvType,
    AdvTypeClient,
    Banner,
    Campaign,
    CampaignType,
    FeedFilter,
    GridRow,
    Group,
    GroupType,
    Retargeting,
    RetargetingStatus
} from '../../typings/domain';
import {
    GdAdPrimaryStatus,
    GdCampaignPrimaryStatus,
    GdOperatorAction,
    OperatorFeaturesFragment
} from '../../typings/graphql-grid';
import {
    BannerAction,
    CampaignAction,
    DealAction,
    FeedFiltersAction,
    GridBannerItemActions,
    GridCampaignItemActions,
    GridFeedFilterItemActions,
    GridGroupItemActions,
    GridRetargetingItemActions,
    GridSourceParams,
    GroupAction,
    GroupsAction,
    RetargetingsAction
} from '../../typings/grid';
import { CampaignAndGroupSelectorEntityToSelectType } from '../../typings/sidebar';
import { ClientFeatures, State } from '../../typings/state';
import { getEditableBannerTypes } from '../bannerEdit/banner';
import { selectGridSourceParams } from '../gridSourceParams/gridSourceParams';
import { selectEditableGroupTypes } from '../groupEdit/group/group';

import * as keyset from './gridActionsMenu.i18n';

const i18n = i18nFactory(keyset);

export const gridActionsMenuSelector = createSelector(
    (state: State, props: GridActionsMenuContainerProps) => state.grid.gridData.data[props.index],
    (state: State, props: GridActionsMenuContainerProps) => props.actions,
    (state: State) => state.commonData.operatorFeatures,
    (state: State) => state.commonData.features,
    (state: State) => state.commonData.accessRights,
    selectGridSourceParams,
    (
        gridRow: GridRow,
        actions: GridActionsMenuContainerActions,
        operatorFeatures: OperatorFeaturesFragment,
        clientFeatures: ClientFeatures,
        accessRights: AccessRights,
        sourceParams: GridSourceParams
    ): {
        actions: Array<GridActionsMenuItems>;
        events: Record<string, () => void>;
    } => {
        let items: Array<GridActionsMenuItems> = [];
        let events: Record<string, () => void> = {};

        if (gridRow !== undefined) {
            switch (sourceParams.view) {
                case campaigns:
                    items = makeCampaignActionsItems(
                        gridRow as Campaign,
                        operatorFeatures,
                        clientFeatures,
                        accessRights
                    );
                    events = makeCampaignEventsItems(
                        gridRow as Campaign,
                        actions as GridCampaignItemActions,
                        sourceParams
                    );
                    break;
                case groups:
                    items = makeGroupActionsItems(
                        gridRow as Group,
                        actions as GridGroupItemActions,
                        sourceParams,
                        selectEditableGroupTypes({ operatorFeatures, clientFeatures, accessRights }),
                        accessRights
                    );
                    events = makeGroupEventsItems(gridRow as Group, actions as GridGroupItemActions, sourceParams);
                    break;
                case banners:
                    items = makeBannerActionsItems(
                        gridRow as Banner,
                        actions as GridBannerItemActions,
                        operatorFeatures,
                        getEditableBannerTypes(operatorFeatures, accessRights)
                    );
                    events = makeBannerEventsItems(gridRow as Banner, actions as GridBannerItemActions);
                    break;
                case feedFilters:
                    items = makeFeedFilterActionsItems();
                    events = makeFeedFilterEventsItems(gridRow as FeedFilter, actions as GridFeedFilterItemActions);
                    break;
                case retargetings:
                    items = makeRetargetingActionsItems(gridRow as Retargeting);
                    events = makeRetargetingEventsItems(gridRow as Retargeting, actions as GridRetargetingItemActions);
                    break;
                default:
                    break;
            }
        }

        return {
            actions: items,
            events
        };
    }
)((state: State, props: GridActionsMenuContainerProps) => props.index);

const makeRetargetingActionsItems = (retargeting: Retargeting): Array<GridActionsMenuItems> => {
    const { isEditable, status } = retargeting;

    return [
        {
            items: [
                {
                    body: i18n('resume'),
                    type: RetargetingsAction.resume,
                    // TODO: Рефаторинг https://st.yandex-team.ru/DIRECT-99709
                    disabled: !isEditable || status === RetargetingStatus.active
                },
                {
                    body: i18n('suspend'),
                    type: RetargetingsAction.suspend,
                    disabled: !isEditable || status === RetargetingStatus.suspended
                },
                {
                    body: i18n('delete'),
                    type: RetargetingsAction.delete,
                    disabled: !isEditable
                }
            ]
        }
    ];
};

const makeRetargetingEventsItems = (
    retargeting: Retargeting,
    actions: GridRetargetingItemActions
): Record<string, () => void> => {
    return {
        [RetargetingsAction.resume]: (): void => actions.onHandleAction(retargeting.id, RetargetingsAction.resume),
        [RetargetingsAction.suspend]: (): void => actions.onHandleAction(retargeting.id, RetargetingsAction.suspend),
        [RetargetingsAction.delete]: (): void => actions.onHandleAction(retargeting.id, RetargetingsAction.delete)
    };
};

const makeCampaignActionsItems = (
    campaign: Campaign,
    operatorFeatures: OperatorFeaturesFragment,
    clientFeatures: ClientFeatures,
    accessRights: AccessRights
): Array<GridActionsMenuItems> => {
    const { isMassEditRegionsEnabled } = clientFeatures;
    const isCampaignEditDisabled = !isCampaignEditable(campaign);
    const isCampaignUpdateInDnaAllowed = checkIsCampaignUpdateAllowedInDna(
        campaign.type,
        operatorFeatures,
        clientFeatures
    );

    const items: Array<GridActionsMenuItem> = [
        {
            body: i18n('add-group'),
            type: CampaignAction.addGroup,
            disabled: isCampaignEditDisabled
        }
    ];

    if (isCampaignEditDisabled && isCampaignUpdateInDnaAllowed) {
        items.push({
            body: i18n('show-params'),
            type: CampaignAction.showParams,
            disabled: false
        });
    } else {
        items.push({
            body: i18n('edit'),
            type: isCampaignUpdateInDnaAllowed ? CampaignAction.editCampaignInDna : CampaignAction.editCampaigns,
            disabled: isCampaignEditDisabled,
            disabledTooltip: i18n('notEditable')
        });
    }

    items.push(
        {
            body: i18n('moderate'),
            type: CampaignAction.goToCampaignModerationOffer,
            disabled:
                isCampaignEditDisabled ||
                (campaign && (campaign.statusInfo.type !== GdCampaignPrimaryStatus.DRAFT || !campaign.hasBanners))
        },
        {
            body: i18n('go-to-report-master'),
            type: CampaignAction.goToStatistics,
            disabled: false
        }
    );

    if (isMassEditRegionsEnabled) {
        items.push({
            body: i18n('regions-transform'),
            type: CampaignAction.regionsTransform,
            disabled: isCampaignEditDisabled || !accessRights.canMassEditAdGroupRegions,
            disabledTooltip: getTextForItemsCampaignsActionTooltipText({
                action: CampaignAction.regionsTransform,
                isCampaignEditDisabled: isCampaignEditDisabled,
                accessForEditRegions: Boolean(accessRights.canMassEditAdGroupRegions)
            })
        });
    }

    return [{ items }];
};

const makeCampaignEventsItems = (
    campaign: Campaign,
    actions: GridCampaignItemActions,
    sourceParams: GridSourceParams
): Record<string, () => void> => {
    return {
        [CampaignAction.addGroup]: (): void =>
            actions.onGroupAdd(
                {
                    id: campaign.id,
                    name: campaign.name,
                    type: campaign.type
                },
                CampaignAndGroupSelectorEntityToSelectType.GROUP
            ),
        [CampaignAction.editCampaigns]: (): void => actions.onEdit(campaign.id, sourceParams.login),
        [CampaignAction.editCampaignInDna]: (): void =>
            actions.onEditInDna(campaign.id, sourceParams.login, campaign.type),
        [CampaignAction.goToCampaignModerationOffer]: (): void => actions.onGoToCampaignModerationOffer(campaign.id),
        [CampaignAction.goToStatistics]: (): void => actions.onGoToStatistics(campaign.id),
        [CampaignAction.showParams]: (): void => actions.onShowParams(campaign.id, sourceParams.login, campaign.type),
        [CampaignAction.regionsTransform]: (): void => actions.onRegionsTransformEditorOpen(campaign.id)
    };
};

const makeGroupActionsItems = (
    group: Group,
    actions: GridGroupItemActions,
    sourceParams: GridSourceParams,
    editableTypes: Set<GroupType>,
    accessRights: AccessRights
): Array<GridActionsMenuItems> => {
    const accessActions = accessRights.actions || [];
    const {
        type: groupType,
        campaign: { type: campaignType },
        access: {
            canBeSentToModeration,
            canBeSentToModerationByClient,
            canBeSentToRemoderation,
            canAcceptModeration,
            canAcceptAdsCalloutsModeration,
            canRemoderateAdsCallouts,
            canBeSentToBS,
            canBeDeleted,
            canCopy
        }
    } = group;
    const isDisabled = !isGroupEditable(group);
    const isInternalGroupType = getInternalType(campaignType);
    const fullyAvailableGroupTypes = GroupsHelpers.getFullyAvailableGroupTypes();
    const fullyAvailable = fullyAvailableGroupTypes.includes(campaignType);

    if (isInternalGroupType) {
        return [
            {
                items: [
                    {
                        body: i18n('add-banner'),
                        type: GroupAction.addBanner,
                        disabled: isDisabled
                    },
                    {
                        body: <>{i18n('edit')}</>,
                        type: GroupsAction.editGroups,
                        disabled: isDisabled,
                        disabledTooltip: i18n('notEditable')
                    }
                ]
            }
        ];
    }

    const groupActionsItems = [
        {
            title: i18n('actions-title'),
            items: compact([
                {
                    body: i18n('add-banner'),
                    type: GroupAction.addBanner,
                    disabled: isDisabled
                },
                editableTypes.has(groupType) && {
                    body: (
                        <>
                            {i18n('edit')}
                            {!fullyAvailable && <BetaMark />}
                        </>
                    ),
                    type: GroupAction.editGroups,
                    disabled: isDisabled,
                    disabledTooltip: i18n('notEditable')
                },
                (!editableTypes.has(groupType) || !fullyAvailable) && {
                    body: i18n('edit'),
                    type: GroupAction.editGroupsOld,
                    disabled: isDisabled,
                    disabledTooltip: i18n('notEditable')
                },
                {
                    body: i18n('copy'),
                    type: GroupAction.copy,
                    disabled: !canCopy
                },
                accessActions.includes(GdOperatorAction.SEND_TO_MODERATION_BY_CLIENT) && {
                    body: i18n('moderateByClient'),
                    type: GroupAction.sendModerateByClient,
                    disabled: !canBeSentToModerationByClient
                },
                accessActions.includes(GdOperatorAction.SEND_TO_MODERATION) && {
                    body: i18n('moderate'),
                    type: GroupAction.sendModerateByAgency,
                    disabled: !canBeSentToModeration
                },
                {
                    body: i18n('delete'),
                    type: GroupAction.delete,
                    disabled: !canBeDeleted
                }
            ])
        }
    ];

    const internalToolItems = compact([
        accessActions.includes(GdOperatorAction.ACCEPT_MODERATION) && {
            body: i18n('moderateAccept'),
            type: GroupAction.moderateAccept,
            disabled: !canAcceptModeration
        },
        accessActions.includes(GdOperatorAction.ACCEPT_ADS_CALLOUTS_MODERATION) && {
            body: i18n('moderateAcceptBannersAdditions'),
            type: GroupAction.moderateAcceptBannersAdditions,
            disabled: !canAcceptAdsCalloutsModeration
        },
        accessActions.includes(GdOperatorAction.SEND_TO_REMODERATION) && {
            body: i18n('remoderate'),
            type: GroupAction.admSendMD,
            disabled: !canBeSentToRemoderation
        },
        accessActions.includes(GdOperatorAction.REMODERATE_ADS_CALLOUTS) && {
            body: i18n('remoderateBannersAdditions'),
            type: GroupAction.remoderateBannersAdditions,
            disabled: !canRemoderateAdsCallouts
        },
        accessActions.includes(GdOperatorAction.SEND_TO_BS) && {
            body: i18n('sendBK'),
            type: GroupAction.sendBK,
            disabled: !canBeSentToBS
        }
    ]);

    if (internalToolItems.length > 0) {
        groupActionsItems.push({
            title: i18n('internal-tool-title'),
            items: internalToolItems
        });
    }

    return groupActionsItems;
};

const makeGroupEventsItems = (
    group: Group,
    actions: GridGroupItemActions,
    sourceParams: GridSourceParams
): Record<string, () => void> => {
    const {
        id: groupId,
        campaign: { id: campaignId, type }
    } = group;
    const isInternalGroupType = getInternalType(type);

    if (isInternalGroupType) {
        return {
            [GroupAction.editGroups]: (): void => actions.onEditInDna(sourceParams.login, campaignId, groupId),
            [GroupAction.addBanner]: (): void =>
                actions.onBannerAdd(
                    {
                        id: group.campaign.id,
                        name: group.campaign.name,
                        type: group.campaign.type
                    },
                    {
                        id: group.id,
                        name: group.name,
                        type: group.campaign.type,
                        groupType: group.type,
                        adsCount: group.adsCount,
                        minusKeywords: group.minusKeywords
                    },
                    CampaignAndGroupSelectorEntityToSelectType.BANNER
                )
        };
    }

    return {
        [GroupAction.addBanner]: (): void =>
            actions.onBannerAdd(
                {
                    id: group.campaign.id,
                    name: group.campaign.name,
                    type: group.campaign.type
                },
                {
                    id: group.id,
                    name: group.name,
                    type: group.campaign.type,
                    groupType: group.type,
                    adsCount: group.adsCount,
                    minusKeywords: group.minusKeywords
                },
                CampaignAndGroupSelectorEntityToSelectType.BANNER
            ),
        [GroupAction.editGroups]: (): void => actions.onEditInDna(sourceParams.login, campaignId, groupId),
        [GroupAction.editGroupsOld]: (): void => actions.onEdit([groupId], campaignId, type, sourceParams.login),
        [GroupAction.sendModerateByClient]: (): void =>
            actions.onHandleAction(groupId, GroupsAction.sendModerateByClient),
        [GroupAction.sendModerateByAgency]: (): void =>
            actions.onHandleAction(groupId, GroupsAction.sendModerateByAgency),
        [GroupAction.delete]: (): void => actions.onHandleAction(groupId, GroupsAction.delete),
        [GroupAction.moderateAccept]: (): void => actions.onHandleAction(groupId, GroupsAction.moderateAccept),
        [GroupAction.moderateAcceptBannersAdditions]: (): void =>
            actions.onHandleAction(groupId, GroupsAction.moderateAcceptBannersAdditions),
        [GroupAction.admSendMD]: (): void => actions.onHandleAction(groupId, GroupsAction.admSendMD),
        [GroupAction.remoderateBannersAdditions]: (): void =>
            actions.onHandleAction(groupId, GroupsAction.remoderateBannersAdditions),
        [GroupAction.sendBK]: (): void => actions.onHandleAction(groupId, GroupsAction.sendBK),
        [GroupsAction.copy]: (): void => actions.onHandleAction(groupId, GroupsAction.copy)
    };
};

const makeBannerActionsItems = (
    banner: Banner,
    actions: GridBannerItemActions,
    features: OperatorFeaturesFragment,
    editableTypes: Array<AdvType | AdvTypeClient>
): Array<GridActionsMenuItems> => {
    const {
        bannerType,
        isAimingAllowed: bannerAimingAllowed,
        group: {
            campaign: { type: campaignType, isAimingAllowed: campaignAimingAllowed }
        }
    } = banner;
    const isDisabled = !isBannerEditable(banner);
    const isAimingAvailable =
        campaignAimingAllowed &&
        ![AdvType.CPM_OUTDOOR, AdvType.CPM_INDOOR].includes(bannerType) &&
        ([CampaignType.CPM_BANNER, CampaignType.CPM_DEALS].includes(campaignType)
            ? features.isBannerAimingCpmAllowed
            : features.isBannerAimingAllowed);
    const fullyAvailableGroupTypes = GroupsHelpers.getFullyAvailableGroupTypes();
    const fullyAvailable = fullyAvailableGroupTypes.includes(campaignType);
    const isArchived = banner.status.type === GdAdPrimaryStatus.ARCHIVED;

    if (bannerType === AdvType.INTERNAL) {
        return [
            {
                items: [
                    {
                        body: i18n('edit'),
                        type: BannerAction.internalBannerEdit,
                        disabled: isDisabled,
                        disabledTooltip: i18n('notEditable')
                    }
                ]
            }
        ];
    }

    return [
        {
            items: compact([
                editableTypes.includes(bannerType) && {
                    body: (
                        <>
                            {i18n('edit')}
                            {!fullyAvailable && <BetaMark />}
                        </>
                    ),
                    type: BannerAction.editBanner,
                    disabled: isDisabled,
                    disabledTooltip: i18n('notEditable')
                },
                (!editableTypes.includes(bannerType) || !fullyAvailable) && {
                    body: i18n('edit'),
                    type: BannerAction.editBannerOld,
                    disabled: isDisabled || isArchived,
                    disabledTooltip: i18n('notEditable')
                },
                isAimingAvailable && {
                    body: i18n('aim'),
                    type: BannerAction.aimBanner,
                    disabled: !bannerAimingAllowed,
                    disabledTooltip: i18n('bannerAimingDisabledMessage')
                }
            ])
        }
    ];
};

const makeBannerEventsItems = (banner: Banner, actions: GridBannerItemActions): Record<string, () => void> => {
    const {
        id,
        group: {
            id: groupId,
            campaign: { id: campaignId, type: campaignType, chiefLogin }
        }
    } = banner;

    return {
        [BannerAction.editBanner]: (): void => actions.onBannerEditDna(chiefLogin, campaignId, id),
        [BannerAction.editBannerOld]: (): void =>
            actions.onBannerEdit(groupId, campaignId, campaignType, chiefLogin, id),
        [BannerAction.aimBanner]: (): void =>
            actions.onBannerAim({
                id,
                bannerId: id,
                campaignId,
                pending: false
            }),
        [BannerAction.internalBannerEdit]: (): void => actions.onInternalBannerEdit(chiefLogin, campaignId, id)
    };
};

const makeFeedFilterActionsItems = (): Array<GridActionsMenuItems> => [
    {
        items: [
            {
                body: i18n('edit'),
                type: FeedFiltersAction.edit,
                disabled: false
            }
        ]
    }
];

const makeFeedFilterEventsItems = (
    feedFilter: FeedFilter,
    actions: GridFeedFilterItemActions
): Record<string, () => void> => {
    const {
        group: { feedId, id: groupId, campaign },
        id,
        name,
        targetFunnel,
        priceCpc,
        priceCpa,
        autobudgetPriority,
        tab,
        conditions,
        feedCategories,
        categoryIds,
        isAvailable,
        vendors,
        priceRange
    } = feedFilter;

    const filter = {
        id,
        name,
        type: targetFunnel,
        priceCpc,
        priceCpa,
        autobudgetPriority,
        tab,
        conditions,
        feedCategories,
        categoryIds,
        isAvailable,
        vendors,
        priceRange,
        campaign: {
            strategy: campaign.strategy.type,
            platform: campaign.strategy.platform
        }
    };

    return {
        [FeedFiltersAction.edit]: (): void => actions.onFilterEdit(groupId, feedId, filter)
    };
};

export const dealActionsMenuSelector = createSelector(
    (state: State, props: DealActionsMenuContainerProps) => props,
    (
        props: DealActionsMenuContainerProps
    ): {
        actions: Array<GridActionsMenuItems>;
        events: Record<string, () => void>;
    } => {
        return {
            actions: [
                {
                    items: [
                        {
                            body: i18n('edit'),
                            type: DealAction.editDeal,
                            disabled: false
                        }
                    ]
                }
            ],
            events: {
                [DealAction.editDeal]: (): void => props.actions.onSettingsClick(props.index)
            }
        };
    }
)((state: State, props: DealActionsMenuContainerProps) => props.index);
